import { useQuery } from "@tanstack/react-query";
import {
  Building2, Users, TrendingUp, Target, MessageSquare,
  ArrowUpRight, ArrowDownRight, Minus, Eye, Zap
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from "recharts";

type Analytics = {
  overview: {
    totalCompanies: number; activeCompanies: number; demoCompanies: number;
    trialCompanies: number; totalUsers: number; activeUsers: number;
    totalLeads: number; newLeads30d: number; newLeads7d: number;
    convertedLeads: number; conversionRate: string; growthRate: string;
    pipelineValue: number; unreadChats: number;
  };
  demoFunnel: { activeDemos: number; expiredDemos: number; converted: number };
  planDistribution: { plan: string; count: number }[];
  countryDistribution: { country: string; count: number }[];
  leadsByStage: { stage: string; count: number }[];
  leadsBySource: { source: string; count: number }[];
  recentLeads: any[];
};

const COLORS = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899"];

const STAGE_LABELS: Record<string, string> = {
  new: "New", contacted: "Contacted", demo_scheduled: "Demo",
  proposal: "Proposal", negotiation: "Negotiation", won: "Won", lost: "Lost",
};

function KpiCard({ title, value, sub, icon: Icon, color, trend }: {
  title: string; value: string | number; sub: string;
  icon: any; color: string; trend?: string;
}) {
  const trendNum = trend ? parseFloat(trend) : 0;
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
            trendNum > 0 ? "bg-emerald-50 text-emerald-700" : trendNum < 0 ? "bg-rose-50 text-rose-600" : "bg-gray-50 text-gray-500"
          }`}>
            {trendNum > 0 ? <ArrowUpRight className="w-3 h-3" /> : trendNum < 0 ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            {Math.abs(trendNum)}%
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{sub}</div>
    </div>
  );
}

export default function CPDashboard() {
  const { data, isLoading } = useQuery<Analytics>({
    queryKey: ["cp-analytics"],
    queryFn: async () => {
      const res = await fetch("/api/cp/analytics", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch analytics");
      return res.json();
    },
    refetchInterval: 30000,
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-gray-200 rounded-lg" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-gray-200 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const o = data.overview;

  const funnelData = [
    { name: "Active Demos", value: data.demoFunnel.activeDemos, fill: "#3b82f6" },
    { name: "Expired", value: data.demoFunnel.expiredDemos, fill: "#f59e0b" },
    { name: "Converted", value: data.demoFunnel.converted, fill: "#10b981" },
  ];

  const stageData = data.leadsByStage.map(s => ({
    name: STAGE_LABELS[s.stage] || s.stage,
    value: s.count,
  }));

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Analytics Dashboard</h2>
        <p className="text-sm text-gray-500 mt-1">Real-time platform metrics and insights</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Active Companies" value={o.activeCompanies} sub={`${o.totalCompanies} total registered`} icon={Building2} color="bg-blue-600" trend={o.growthRate} />
        <KpiCard title="Total Users" value={o.activeUsers} sub={`${o.totalUsers} total accounts`} icon={Users} color="bg-violet-600" />
        <KpiCard title="Conversion Rate" value={`${o.conversionRate}%`} sub={`${o.convertedLeads} of ${o.totalLeads} leads`} icon={Target} color="bg-emerald-600" />
        <KpiCard title="Pipeline Value" value={`$${(o.pipelineValue / 1000).toFixed(1)}k`} sub={`${o.newLeads7d} new leads this week`} icon={TrendingUp} color="bg-amber-500" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Demo Companies" value={o.demoCompanies} sub="Active demo tenants" icon={Eye} color="bg-sky-500" />
        <KpiCard title="Trial Companies" value={o.trialCompanies} sub="In trial period" icon={Zap} color="bg-orange-500" />
        <KpiCard title="New Leads (30d)" value={o.newLeads30d} sub={`${o.newLeads7d} in last 7 days`} icon={TrendingUp} color="bg-indigo-600" />
        <KpiCard title="Unread Chats" value={o.unreadChats} sub="Awaiting response" icon={MessageSquare} color="bg-rose-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4 text-sm">Plan Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.planDistribution.map(p => ({ name: p.plan || "pro", value: p.count }))}
                  cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`} labelLine={false}
                >
                  {data.planDistribution.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4 text-sm">Demo Funnel</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {funnelData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4 text-sm">Leads by Stage</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4 text-sm">Top Countries</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.countryDistribution.map(c => ({ name: c.country || "Unknown", value: c.count }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#3b82f6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {data.recentLeads.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4 text-sm">Recent Leads</h3>
          <div className="space-y-3">
            {data.recentLeads.map((lead: any) => (
              <div key={lead.id} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white text-xs font-bold">
                    {(lead.name || lead.email)?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{lead.name || lead.email}</div>
                    <div className="text-xs text-gray-400">{lead.email} {lead.country ? `· ${lead.country}` : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-600">
                    {STAGE_LABELS[lead.stage] || lead.stage}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(lead.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
